/**
 * The one mutation behind every user data editor action (Backlog 050).
 *
 * Every change the editor can make — revoke, void, edit, delete — has the same shape on the wire:
 * a reason in, `{ changes, pointsBefore, pointsAfter }` out. So a panel does not build a mutation
 * per action; it hands `submit` a thunk that makes the call, and this hook does the rest the same
 * way everywhere: surface the server's refusal text, invalidate, and keep the result so the panel
 * can show the admin what actually moved.
 *
 * It invalidates the whole data prefix rather than the list the action came from, because the
 * consequences cross lists: voiding a run deletes its step event and can take an achievement.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AdminUserDataChangeResultDto } from '@api/types/userData'
import { ADMIN_USERS_KEY, adminUserDataKey, adminUserKey } from './userQueryKeys'
import { apiErrorMessage } from './userFormatting'

type ChangeCall = () => Promise<AdminUserDataChangeResultDto>

export function useUserDataChange(userId: string) {
  const queryClient = useQueryClient()
  const [result, setResult] = useState<AdminUserDataChangeResultDto | null>(null)
  // Separate from `result` so the result dialog keeps its content while it animates closed.
  const [resultOpen, setResultOpen] = useState(false)

  const mutation = useMutation({
    mutationFn: (call: ChangeCall) => call(),
    onSuccess: (changeResult) => {
      setResult(changeResult)
      setResultOpen(true)
      queryClient.invalidateQueries({ queryKey: adminUserDataKey(userId) })
      // Points, rank and step totals on the user record move with most of these changes.
      queryClient.invalidateQueries({ queryKey: adminUserKey(userId) })
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
    },
  })

  return {
    /** Runs a change. `onDone` fires only on success — typically it closes the dialog that asked. */
    submit: (call: ChangeCall, onDone?: () => void) => mutation.mutate(call, { onSuccess: () => onDone?.() }),
    pending: mutation.isPending,
    error: mutation.error ? apiErrorMessage(mutation.error, 'The change could not be made.') : null,
    /** Clears a failure, so the next dialog does not open showing the last one's error. */
    resetError: mutation.reset,
    result,
    resultOpen,
    closeResult: () => setResultOpen(false),
  }
}
